import { GovAccordion, GovAccordionItem } from '@gov-design-system-ce/react';
import type { TreeFormCheckboxes } from '@gov-nx/core/hooks';
import { FormCheckbox } from '../Form/FormCheckbox';
import { FormSideOffsetLayout } from '../Layout/FormSideOffsetLayout';

interface ExpandableNodeProps {
	name: string;
	label: string;
	wcagLabel: string;
	isDisabled: boolean;
	indeterminate?: boolean;
	isOpen: boolean;
	isToggleable: boolean;
	children: React.ReactNode;
}

const ExpandableNode = ({
	name,
	label,
	isDisabled,
	indeterminate,
	isOpen,
	isToggleable,
	children,
}: ExpandableNodeProps) => {
	const checkbox = (
		<FormCheckbox
			field={{
				isDisabled,
				indeterminate,
				name,
				label: {
					children: label,
				},
			}}
		/>
	);

	if (isToggleable) {
		return (
			<GovAccordion size="m">
				<GovAccordionItem
					open={isOpen}
					disabled={isDisabled}
					label={<span onClick={(e) => e.stopPropagation()}>{checkbox}</span>}>
					{children}
				</GovAccordionItem>
			</GovAccordion>
		);
	}

	return (
		<>
			{checkbox}
			{children}
		</>
	);
};

interface Props {
	treeCheckboxes: TreeFormCheckboxes;
	isDisabled: boolean;
	wcagLabelOpened: string;
	wcagLabelClosed: string;
}

export const CheckboxTree = ({
	treeCheckboxes,
	isDisabled,
	wcagLabelOpened,
	wcagLabelClosed,
}: Props) => {
	const { operations, hasPartiallyCheckedDescendants, getTree } =
		treeCheckboxes;
	const { isNodeOpen } = operations;
	const tree = getTree();
	return (
		<ul className="gov-list--plain p-0!">
			<li>
				<ExpandableNode
					isOpen={isNodeOpen(tree.name)}
					isToggleable={tree.isToggleable}
					wcagLabel={isNodeOpen(tree.name) ? wcagLabelOpened : wcagLabelClosed}
					indeterminate={hasPartiallyCheckedDescendants(tree.name)}
					isDisabled={isDisabled}
					name={tree.name}
					label={tree.label}>
					{
						<FormSideOffsetLayout className="pl-(--spacing-m)!">
							<ul className="gov-list--plain p-0!">
								{tree.children.map((group) => (
									<li key={group.name}>
										<ExpandableNode
											isOpen={isNodeOpen(group.name)}
											isToggleable={group.isToggleable}
											indeterminate={hasPartiallyCheckedDescendants(group.name)}
											wcagLabel={
												isNodeOpen(group.name)
													? wcagLabelOpened
													: wcagLabelClosed
											}
											isDisabled={isDisabled}
											name={group.name}
											label={group.label}>
											{
												<FormSideOffsetLayout className="pl-(--spacing-m)! pb-(--spacing-m)">
													<ul className="gov-list--plain p-0!">
														{group.children.map((field) => (
															<li key={field.name}>
																<FormCheckbox
																	field={{
																		name: field.name,
																		isDisabled: isDisabled,
																		size: 'm',
																		label: {
																			children: field.label,
																		},
																	}}
																/>
															</li>
														))}
													</ul>
												</FormSideOffsetLayout>
											}
										</ExpandableNode>
									</li>
								))}
							</ul>
						</FormSideOffsetLayout>
					}
				</ExpandableNode>
			</li>
		</ul>
	);
};
